import { assert, assertNever } from '@varos/util-typescript';
import {
    HStack,
    Text,
    Box,
    Checkbox,
    Tag,
    Tooltip,
    Icon,
    Button,
} from '@chakra-ui/react';
import {
    BooleanElementViewProps,
    ElementComponents,
    EnumElementViewProps,
    NumberElementViewProps,
    SnippetElementViewProps,
    TextElementViewProps,
    DatetimeElementViewProps,
} from '../../../../view';
import { ReportingViewBaseConfig } from '../../../../base';
import { ValueItemViewProps } from './valueItemProps';
import React from 'react';

export function createValueItemView(
    config: Omit<ReportingViewBaseConfig, 'Container'> & {
        Container: ElementComponents;
    }
): React.FC<ValueItemViewProps> {
    const {
        Container: {
            Text: TextElementContainer,
            Number: NumberElementContainer,
            Boolean: BooleanElementContainer,
            Datetime: DatetimeElementContainer,
            Enum: EnumElementContainer,
            Snippet: SnippetElementContainer,
        },
    } = config;

    const TextElement: React.FC<TextElementViewProps> = (props) => {
        return (
            <Text noOfLines={1} maxW="12rem">
                {props.label}
            </Text>
        );
    };

    const NumberElement: React.FC<NumberElementViewProps> = (props) => {
        return <Text>{props.label}</Text>;
    };

    const BooleanElement: React.FC<BooleanElementViewProps> = (props) => {
        return <Checkbox {...props.input} isReadOnly={true} />;
    };

    const DatetimeElement: React.FC<DatetimeElementViewProps> = (props) => {
        return (
            <Text noOfLines={1} maxW="12rem">
                {props.label}
            </Text>
        );
    };

    const EnumElement: React.FC<EnumElementViewProps> = (props) => {
        if (props.isEmpty) {
            return <Text>{props.label}</Text>;
        }

        return (
            <Tag
                variant="solid"
                colorScheme={props.value?.colorScheme}
                size="md"
                borderRadius="full"
                px={3}
            >
                <HStack spacing={2}>
                    {/* <Box w={2} h={2} bg={`whiteAlpha.400`} borderRadius="full" /> */}
                    <Text noOfLines={1} maxW="12rem">
                        {props.label}
                    </Text>
                </HStack>
            </Tag>
        );
    };

    const SnippetElement: React.FC<SnippetElementViewProps> = (props) => {
        return (
            <Button
                {...props.fullscreen.button}
                onFocus={(event) => event.preventDefault()}
                fontSize="sm"
                px={0}
                bg="none"
                color="whiteAlpha.500"
                fontWeight="medium"
                leftIcon={<Icon as={props.property.Icon} fontSize="md" />}
                iconSpacing={1.5}
                _focus={{ outline: 'none' }}
                _focusVisible={{ outline: 'none' }}
                _hover={{ color: 'whiteAlpha.600' }}
                _active={{ color: 'whiteAlpha.600' }}
            >
                {props.property.label}
            </Button>
        );
    };

    const Inner: React.FC<ValueItemViewProps> = (props) => {
        if (props.element.kind === 'text') {
            return <TextElementContainer as={TextElement} {...props.element} />;
        }
        if (props.element.kind === 'number') {
            return <NumberElementContainer as={NumberElement} {...props.element} />;
        }
        if (props.element.kind === 'boolean') {
            return <BooleanElementContainer as={BooleanElement} {...props.element} />;
        }
        if (props.element.kind === 'datetime') {
            return <DatetimeElementContainer as={DatetimeElement} {...props.element} />;
        }
        if (props.element.kind === 'enum') {
            return <EnumElementContainer as={EnumElement} {...props.element} />;
        }
        if (props.element.kind === 'snippet') {
            return <SnippetElementContainer as={SnippetElement} {...props.element} />;
        }
        assertNever(props.element);
    };

    return (props) => {
        return <Inner {...props} />;
    };
}
